import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import SEO from '@/components/feature/SEO';
import AdminPageEditBar from '@/components/feature/AdminPageEditBar';
import CTASection, { CTASectionData, defaultCTAData } from '@/pages/home/components/CTASection';
import ServicesHero, { ServicesHeroData } from './components/ServicesHero';
import ServicesDetail, { ServicesDetailData } from './components/ServicesDetail';

export interface ServicesPageContent {
  hero: ServicesHeroData;
  detail: ServicesDetailData;
  cta: CTASectionData;
}

export const defaultServicesContentES: ServicesPageContent = {
  hero: {
    badge: 'Soluciones Digitales',
    titlePrefix: 'Todo lo que tu Negocio Necesita',
    titleHighlight: 'para Crecer Online',
    subtitle: 'Desde sitios web personalizados y branding hasta automatización con IA y estrategia — construimos la infraestructura digital que convierte tu visión en resultados.',
    ctaBook: 'Agendar una Consultoría Gratuita',
    ctaTeam: 'Conoce al Equipo',
    stats: [
      { value: '9+', label: 'Servicios Ofrecidos' },
      { value: '100%', label: 'Soluciones Personalizadas' },
      { value: '24/7', label: 'Soporte y Hosting' }
    ]
  },
  detail: {
    featured: {
      badge: 'Servicio Destacado',
      mostPopular: 'Más Popular',
      title: 'Creación de Sitio Web + Hosting',
      color: '#00c2ff',
      accentColor: '#8c52ff',
      image: 'https://readdy.ai/api/search-image?query=modern%20professional%20website%20design%20laptop%20screen%20glowing%20neon%20light%20dark%20background%20custom%20web%20development%20high%20converting%20landing%20page%20clean%20minimal%20elegant%20digital%20agency%20studio&width=1200&height=600&seq=feat1&orientation=landscape',
      desc: 'Sitios web personalizados, modernos y de alta conversión diseñados para empresas y emprendedores. Incluye desarrollo completo, optimización móvil, estructura lista para SEO e integración de hosting.',
      benefits: [
        'Diseño personalizado adaptado a tu marca',
        'Optimizado para móviles y totalmente responsivo',
        'Estructura lista para SEO desde el primer día',
        'Rendimiento rápido y hosting incluido',
        'Formularios de captura e integración de CTAs',
        'Solución integral — sin dolores de cabeza técnicos'
      ],
      forWho: 'Dueños de negocios, emprendedores y marcas que desean una presencia en línea sólida, profesional y escalable.',
      buttonText: 'Reservar una Consulta',
      buttonLink: '/contact'
    },
    header: {
      prefix: 'Todos Nuestros',
      highlight: 'Servicios',
      subtitle: 'Todo lo que necesitas para construir, hacer crecer y escalar tu negocio en línea — bajo un mismo techo.'
    },
    items: [
      {
        icon: 'ri-customer-service-2-line',
        title: 'Creación de CRM',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=CRM%20dashboard%20client%20management%20system%20modern%20dark%20UI%20neon%20pink%20purple%20glow%20leads%20pipeline%20workflow%20automation%20professional%20software%20interface&width=700&height=450&seq=crm1&orientation=landscape',
        desc: 'Sistemas CRM hechos a medida para gestionar leads, clientes, flujos de trabajo y operaciones internas de manera eficiente.',
        benefits: [
          'Mejor organización y seguimiento de clientes',
          'Automatización de procesos repetitivos',
          'Aumento de la productividad del equipo',
          'Datos e informes centralizados'
        ],
        forWho: 'Empresas que desean escalar sus operaciones.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-palette-line',
        title: 'Branding y Diseño',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=brand%20identity%20design%20logo%20color%20palette%20typography%20visual%20direction%20modern%20elegant%20dark%20background%20purple%20gradient%20creative%20agency%20professional%20branding%20kit&width=700&height=450&seq=brand1&orientation=landscape',
        desc: 'Creación de identidad de marca, incluyendo logotipos, paletas de colores, dirección visual y materiales de marketing.',
        benefits: [
          'Mayor reconocimiento de marca',
          'Imagen profesional y consistente',
          'Logo + kit de marca completo',
          'Plantillas de material de marketing'
        ],
        forWho: 'Nuevos negocios que necesitan un refresh o una identidad más sólida.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-instagram-line',
        title: 'Consultoría de Redes Sociales',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=social%20media%20strategy%20consulting%20session%20digital%20marketing%20growth%20analytics%20instagram%20tiktok%20dark%20background%20neon%20glow%20professional%20entrepreneur%20business&width=700&height=450&seq=smc1&orientation=landscape',
        desc: 'Orientación estratégica para ayudar a las empresas a crecer y posicionarse de manera efectiva en las redes sociales.',
        benefits: [
          'Estrategia y dirección de contenido claras',
          'Hoja de ruta de crecimiento accionable',
          'Información sobre segmentación de audiencia',
          'Tácticas de engagement y conversión'
        ],
        forWho: 'Dueños que quieren gestionar su propio contenido con guía experta.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-video-line',
        title: 'Consultoría de Creación de Contenido',
        color: '#00c2ff',
        image: 'https://readdy.ai/api/search-image?query=content%20creation%20consulting%20video%20strategy%20creative%20direction%20dark%20studio%20neon%20blue%20glow%20camera%20equipment%20professional%20creator%20business%20coaching&width=700&height=450&seq=ccc1&orientation=landscape',
        desc: 'Guía sobre cómo crear contenido atractivo y estratégico que atraiga y convierta a tu audiencia ideal.',
        benefits: [
          'Mejores ideas y estructura de contenido',
          'Mejora en la consistencia de publicación',
          'Mayores tasas de interacción',
          'Contenido que convierte seguidores'
        ],
        forWho: 'Creadores y empresas con dificultades en la estructura de su contenido.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-bar-chart-box-line',
        title: 'Optimización de Redes Sociales',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=social%20media%20profile%20optimization%20analytics%20dashboard%20dark%20background%20purple%20neon%20glow%20business%20branding%20conversion%20improvement%20professional%20digital%20marketing&width=700&height=450&seq=smo1&orientation=landscape',
        desc: 'Optimización de perfiles en redes sociales para mejorar la claridad, el branding y el rendimiento de conversión.',
        benefits: [
          'Perfiles más profesionales',
          'Mejores primeras impresiones',
          'Mejor estrategia de bio y enlaces',
          'Mayor conversión de visitantes'
        ],
        forWho: 'Negocios con perfiles existentes que necesitan mejoras.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-map-pin-2-line',
        title: 'Perfil de Google Business',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=Google%20Business%20Profile%20local%20SEO%20map%20listing%20setup%20dark%20background%20neon%20pink%20glow%20local%20business%20visibility%20search%20results%20professional%20digital%20marketing&width=700&height=450&seq=gbp1&orientation=landscape',
        desc: 'Configuración y optimización del Perfil de Empresa en Google para mejorar la visibilidad local y atraer más clientes.',
        benefits: [
          'Mejor posicionamiento en SEO local',
          'Mayor visibilidad en Google Maps',
          'Más consultas de clientes',
          'Ficha de negocio profesional'
        ],
        forWho: 'Negocios locales que quieren aparecer en búsquedas y mapas.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-robot-line',
        title: 'Automatización con IA y Funnels',
        color: '#00c2ff',
        image: 'https://readdy.ai/api/search-image?query=AI%20automation%20sales%20funnel%20digital%20workflow%20robot%20technology%20dark%20background%20neon%20blue%20glow%20futuristic%20business%20automation%20modern%20systems%20leads%20conversion&width=700&height=450&seq=ai1&orientation=landscape',
        desc: 'Creación de sistemas automatizados, funnels y flujos de trabajo utilizando IA y herramientas digitales que funcionan 24/7.',
        benefits: [
          'Flujos de trabajo automatizados que ahorran tiempo',
          'Mejores tasas de conversión de leads',
          'Customer journeys automatizados',
          'Sistemas de seguimiento impulsados por IA'
        ],
        forWho: 'Empresas que desean escalar con automatización y sistemas más inteligentes.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-lightbulb-line',
        title: 'Consultoría y Estrategia',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=business%20consulting%20strategy%20session%20entrepreneur%20coaching%20dark%20background%20purple%20neon%20glow%20whiteboard%20planning%20digital%20growth%20roadmap%20professional%20advisor&width=700&height=450&seq=cons1&orientation=landscape',
        desc: 'Sesiones de consultoría personalizadas enfocadas en el crecimiento empresarial, la estrategia digital y la planificación de la ejecución.',
        benefits: [
          'Dirección clara y conocimientos expertos',
          'Planes de crecimiento accionables',
          'Auditoría digital y análisis de brechas',
          'Responsabilidad y seguimiento'
        ],
        forWho: 'Emprendedores listos para crecer y escalar.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      }
    ],
    footerCta: {
      title: '¿No estás seguro de qué servicio es el adecuado para ti?',
      subtitle: 'Agenda una llamada estratégica gratuita y te ayudaremos a determinar exactamente lo que tu negocio necesita para crecer.',
      book: 'Obtener una Consultoría Gratuita',
      link: '/contact'
    }
  },
  cta: defaultCTAData
};

export const defaultServicesContentEN: ServicesPageContent = {
  hero: {
    badge: 'Digital Solutions',
    titlePrefix: 'Everything Your Business Needs',
    titleHighlight: 'to Grow Online',
    subtitle: 'From custom websites and branding to AI automation and strategy — we build the digital infrastructure that turns your vision into results.',
    ctaBook: 'Book a Free Consultation',
    ctaTeam: 'Meet the Team',
    stats: [
      { value: '9+', label: 'Services Offered' },
      { value: '100%', label: 'Custom Solutions' },
      { value: '24/7', label: 'Support & Hosting' }
    ]
  },
  detail: {
    featured: {
      badge: 'Featured Service',
      mostPopular: 'Most Popular',
      title: 'Website Creation + Hosting',
      color: '#00c2ff',
      accentColor: '#8c52ff',
      image: 'https://readdy.ai/api/search-image?query=modern%20professional%20website%20design%20laptop%20screen%20glowing%20neon%20light%20dark%20background%20custom%20web%20development%20high%20converting%20landing%20page%20clean%20minimal%20elegant%20digital%20agency%20studio&width=1200&height=600&seq=feat1&orientation=landscape',
      desc: 'Custom, modern, high-converting websites designed for businesses and entrepreneurs. Includes full website development, mobile optimization, SEO-ready structure, and hosting integration.',
      benefits: [
        'Custom design tailored to your brand',
        'Mobile-optimized & fully responsive',
        'SEO-ready structure from day one',
        'Fast performance & hosting included',
        'Lead capture forms & CTA integration',
        'All-in-one solution — no tech headaches'
      ],
      forWho: 'Business owners, entrepreneurs, and brands that want a strong, professional, and scalable online presence.',
      buttonText: 'Book a Consultation',
      buttonLink: '/contact'
    },
    header: {
      prefix: 'All Our',
      highlight: 'Services',
      subtitle: 'Everything you need to build, grow, and scale your business online — under one roof.'
    },
    items: [
      {
        icon: 'ri-customer-service-2-line',
        title: 'CRM Creation',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=CRM%20dashboard%20client%20management%20system%20modern%20dark%20UI%20neon%20pink%20purple%20glow%20leads%20pipeline%20workflow%20automation%20professional%20software%20interface&width=700&height=450&seq=crm1&orientation=landscape',
        desc: 'Custom-built CRM systems to manage leads, clients, workflows, and internal operations efficiently.',
        benefits: [
          'Better organization & client tracking',
          'Automation of repetitive processes',
          'Increased team productivity',
          'Centralized data & reporting'
        ],
        forWho: 'Businesses looking to scale operations.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-palette-line',
        title: 'Branding & Design',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=brand%20identity%20design%20logo%20color%20palette%20typography%20visual%20direction%20modern%20elegant%20dark%20background%20purple%20gradient%20creative%20agency%20professional%20branding%20kit&width=700&height=450&seq=brand1&orientation=landscape',
        desc: 'Brand identity creation including logos, color palettes, typography, and marketing materials.',
        benefits: [
          'Higher brand recognition',
          'Professional and consistent image',
          'Full brand kit + logo',
          'Marketing collateral templates'
        ],
        forWho: 'New businesses or those needing a rebrand.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-instagram-line',
        title: 'Social Media Consulting',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=social%20media%20strategy%20consulting%20session%20digital%20marketing%20growth%20analytics%20instagram%20tiktok%20dark%20background%20neon%20glow%20professional%20entrepreneur%20business&width=700&height=450&seq=smc1&orientation=landscape',
        desc: 'Strategic guidance to help businesses grow and position themselves effectively on social media platforms.',
        benefits: [
          'Clear content strategy & direction',
          'Actionable growth roadmap',
          'Audience targeting insights',
          'Engagement & conversion tactics'
        ],
        forWho: 'Owners who want to manage their own content with expert guidance.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-video-line',
        title: 'Content Creation Consulting',
        color: '#00c2ff',
        image: 'https://readdy.ai/api/search-image?query=content%20creation%20consulting%20video%20strategy%20creative%20direction%20dark%20studio%20neon%20blue%20glow%20camera%20equipment%20professional%20creator%20business%20coaching&width=700&height=450&seq=ccc1&orientation=landscape',
        desc: 'Guidance on creating engaging, strategic content that attracts and converts your ideal audience.',
        benefits: [
          'Better content ideas & frameworks',
          'Improved posting consistency',
          'Higher engagement rates',
          'Content that converts followers'
        ],
        forWho: 'Creators and businesses struggling with content consistency.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-bar-chart-box-line',
        title: 'Social Media Optimization',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=social%20media%20profile%20optimization%20analytics%20dashboard%20dark%20background%20purple%20neon%20glow%20business%20branding%20conversion%20improvement%20professional%20digital%20marketing&width=700&height=450&seq=smo1&orientation=landscape',
        desc: 'Optimization of social media profiles to enhance brand clarity, visual appeal, and conversion performance.',
        benefits: [
          'More professional profiles',
          'Better first impressions',
          'Improved bio & link strategy',
          'Higher visitor-to-follower conversion'
        ],
        forWho: 'Businesses with existing profiles that need an upgrade.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-map-pin-2-line',
        title: 'Google Business Profile',
        color: '#FF2DAA',
        image: 'https://readdy.ai/api/search-image?query=Google%20Business%20Profile%20local%20SEO%20map%20listing%20setup%20dark%20background%20neon%20pink%20glow%20local%20business%20visibility%20search%20results%20professional%20digital%20marketing&width=700&height=450&seq=gbp1&orientation=landscape',
        desc: 'Setup and optimization of your Google Business Profile to improve local SEO visibility and attract local customers.',
        benefits: [
          'Higher local search ranking',
          'Increased visibility on Google Maps',
          'More customer calls and directions',
          'Professional business listing'
        ],
        forWho: 'Local businesses wanting to be found easily on Google.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-robot-line',
        title: 'AI Automation & Funnels',
        color: '#00c2ff',
        image: 'https://readdy.ai/api/search-image?query=AI%20automation%20sales%20funnel%20digital%20workflow%20robot%20technology%20dark%20background%20neon%20blue%20glow%20futuristic%20business%20automation%20modern%20systems%20leads%20conversion&width=700&height=450&seq=ai1&orientation=landscape',
        desc: 'Custom automated workflows, funnels, and integrations powered by AI to run your business 24/7.',
        benefits: [
          'Time-saving automated workflows',
          'Higher lead conversion rates',
          'Seamless customer journeys',
          'AI-powered follow-up systems'
        ],
        forWho: 'Businesses wanting to scale with smart automation.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      },
      {
        icon: 'ri-lightbulb-line',
        title: 'Consulting & Strategy',
        color: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=business%20consulting%20strategy%20session%20entrepreneur%20coaching%20dark%20background%20purple%20neon%20glow%20whiteboard%20planning%20digital%20growth%20roadmap%20professional%20advisor&width=700&height=450&seq=cons1&orientation=landscape',
        desc: 'Personalized consulting sessions focused on digital strategy, business growth, and execution planning.',
        benefits: [
          'Clear strategic direction',
          'Actionable execution roadmap',
          'Digital audit & gap analysis',
          'Accountability and ongoing review'
        ],
        forWho: 'Entrepreneurs ready to take decisive action to grow.',
        buttonText: 'Book a Consultation',
        buttonLink: '/contact'
      }
    ],
    footerCta: {
      title: 'Not sure which service is right for you?',
      subtitle: "Schedule a free strategy call and we'll help you determine exactly what your business needs to grow.",
      book: 'Get a Free Consultation',
      link: '/contact'
    }
  },
  cta: {
    badge: 'TRANSFORM YOUR BUSINESS',
    titlePrefix: 'Ready to transform your ',
    titleHighlight: 'digital presence?',
    subtitle: 'Discover how our comprehensive digital marketing, branding, and AI automation solutions can scale your business.',
    btnAcademyText: 'Explore Academy',
    btnAcademyLink: 'https://smartbusiness.academy',
    btnConsultText: 'Book Consultation',
    btnConsultLink: '/contact',
    btnServicesText: 'View Services',
    btnServicesLink: '/services',
    trustIndicators: [
      { icon: 'ri-shield-check-line', text: '100% Customized' },
      { icon: 'ri-customer-service-line', text: 'Ongoing Support' },
      { icon: 'ri-global-line', text: 'Global Focus' }
    ]
  }
};

const ServicesPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es';
  const docId = lang === 'en' ? 'services_page_en' : 'services_page';
  const defaultContent = lang === 'en' ? defaultServicesContentEN : defaultServicesContentES;

  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [content, setContent] = useState<ServicesPageContent>(defaultContent);
  const [initialContent, setInitialContent] = useState<ServicesPageContent>(defaultContent);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedNotification, setSavedNotification] = useState(false);

  useEffect(() => {
    // Reset immediately to the target language defaults while fetching
    setContent(defaultContent);
    setInitialContent(defaultContent);

    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', docId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<ServicesPageContent>;
          const merged: ServicesPageContent = {
            hero: { ...defaultContent.hero, ...(data.hero || {}), stats: data.hero?.stats || defaultContent.hero.stats },
            detail: {
              ...defaultContent.detail,
              ...(data.detail || {}),
              featured: {
                ...defaultContent.detail.featured,
                ...(data.detail?.featured || {})
              },
              header: {
                ...defaultContent.detail.header,
                ...(data.detail?.header || {})
              },
              items: data.detail?.items || defaultContent.detail.items,
              footerCta: {
                ...defaultContent.detail.footerCta,
                ...(data.detail?.footerCta || {})
              }
            },
            cta: {
              ...defaultContent.cta,
              ...(data.cta || {}),
              trustIndicators: data.cta?.trustIndicators || defaultContent.cta.trustIndicators
            }
          };
          setContent(merged);
          setInitialContent(merged);
        }
      } catch (error) {
        console.error('Error fetching services page content:', error);
      }
    };

    fetchContent();
  }, [lang, docId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landing_content', docId);
      await setDoc(docRef, content);
      setInitialContent(content);
      setIsEditing(false);
      setSavedNotification(true);
      setTimeout(() => setSavedNotification(false), 3500);
    } catch (error) {
      console.error('Error saving services page content:', error);
      alert(lang === 'en' ? 'Error saving changes to Firestore.' : 'Error al guardar los cambios en Firestore.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setContent(initialContent);
    setIsEditing(false);
  };

  return (
    <main className="relative">
      <SEO 
        title={lang === 'en' ? "Our Services | Sbdigisolutions" : "Nuestros Servicios | Sbdigisolutions"} 
        description={lang === 'en' ? "Explore our digital marketing, web design, CRM creation, and AI automation services." : "Descubre todos nuestros servicios de marketing digital, diseño web, creación de CRM y automatización con IA."} 
      />

      <AdminPageEditBar
        isAdmin={isAdmin}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        pageName={lang === 'en' ? 'Services (EN)' : 'Servicios (ES)'}
      />

      {savedNotification && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-2xl flex items-center gap-2 animate-bounce">
          <i className="ri-checkbox-circle-line text-lg" />
          <span>{lang === 'en' ? 'Services page saved successfully!' : '¡Página de Servicios guardada correctamente!'}</span>
        </div>
      )}

      <ServicesHero
        data={content.hero}
        isEditing={isEditing}
        onChange={(hero) => setContent({ ...content, hero })}
      />

      <ServicesDetail
        data={content.detail}
        isEditing={isEditing}
        onChange={(detail) => setContent({ ...content, detail })}
      />

      <CTASection
        data={content.cta}
        isEditing={isEditing}
        onChange={(cta) => setContent({ ...content, cta })}
      />
    </main>
  );
};

export default ServicesPage;
